import { z } from 'zod';

// A tag descriptor rendered in the surface header. `bind` reads a value from
// the model; the optional `deriver` is a runtime ref (SurfaceRuntime key) that
// maps that value to a tag-style descriptor (label + colour). Entities map
// status enums to tag colours declaratively via the deriver rather than baking
// presentation into the schema. Refs are validated as strings — they resolve
// to runtime capabilities at compile/mount time.
export const HeaderTagSchemaZod = z.object({
  bind: z.string().min(1),
  deriver: z.string().min(1).optional(),
}).strict();

export type HeaderTagSchema = z.infer<typeof HeaderTagSchemaZod>;

// A labelled information chip rendered in a header information row. It uses
// the same runtime deriver contract as tags, but keeps its label separate from
// the derived value shown inside the chip.
export const HeaderChipSchemaZod = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  bind: z.string().min(1),
  deriver: z.string().min(1).optional(),
}).strict();

export type HeaderChipSchema = z.infer<typeof HeaderChipSchemaZod>;

// Schema-level header for a surface. `identifier` is a model bind path whose
// value is the header's primary label (e.g. an invoice number). Tags and chips
// are optional ordered lists rendered by hosts that support them. `editable`
// mounts that identifier as an inline title editor on hosts that support it.
// `kind` names what the record is ("Visit approval") ahead of the identifier;
// absent defaults to the entity metadata display name, `false` suppresses it.
export const HeaderSchemaZod = z.object({
  identifier: z.string().min(1),
  kind: z.union([z.string().min(1), z.literal(false)]).optional(),
  editable: z.boolean().optional(),
  // Detail hosts may place all actions in More, beside the tags in a compact header.
  actionsMenuOnly: z.boolean().optional(),
  tags: z.array(HeaderTagSchemaZod).optional(),
  chips: z.array(HeaderChipSchemaZod).optional(),
}).strict();

export type HeaderSchema = z.infer<typeof HeaderSchemaZod>;
